import {
  InfiniteData,
  InfiniteQueryConfigOptions,
} from '@reduxjs/toolkit/query'

export interface PaginationResponse<T> {
  rows: T[]
  total: number
}

export interface PaginationRequest {
  page?: number
  size: number
}

export interface InfinitePaginationResponse<T> extends PaginationResponse<T> {
  nextId: number | undefined
}

export interface InfinitePaginationRequest {
  limit: number
  firstId?: number
}

export interface OffsetPaginationRequest {
  limit: number
  offset: number
}

export const infiniteOffsetPaginationQueryOptions: InfiniteQueryConfigOptions<{ total: number }, OffsetPaginationRequest> = {
  initialPageParam: {
    offset: 0,
    limit: 15,
  },
  getNextPageParam: (
    lastPage,
    allPages,
    lastPageParam,
  ) => {
    const nextOffset = lastPageParam.offset + lastPageParam.limit
    const remainingItems = lastPage?.total - nextOffset

    if (remainingItems <= 0) {
      return undefined
    }

    return {
      ...lastPageParam,
      offset: nextOffset,
    }
  },
}

export const infinitePaginationQueryOptions: InfiniteQueryConfigOptions<{ nextId: number | undefined }, InfinitePaginationRequest> = {
  initialPageParam: {
    firstId: undefined,
    limit: 10,
  },
  getNextPageParam: (
    lastPage,
    allPages,
    lastPageParam,
  ) => (lastPage.nextId ? {
    ...lastPageParam,
    firstId: lastPage.nextId,
  } : undefined),
}

export enum PaginationDisplayType {
  Empty,
  LoadingInitial,
  Data,
}

export interface GetPaginationDisplayTypeParams<T, PageParam> {
  data: InfiniteData<PaginationResponse<T>, PageParam> | undefined
  isLoading: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
}

export const getPaginationDisplayType = <T, PageParam>({
  data,
  isLoading,
  isFetching,
  isFetchingNextPage,
}: GetPaginationDisplayTypeParams<T, PageParam>) => {
  if (isLoading) return PaginationDisplayType.LoadingInitial
  if (isFetching && !isFetchingNextPage) return PaginationDisplayType.LoadingInitial
  if (!data) return PaginationDisplayType.Empty
  const { pages } = data
  const { length } = pages
  if (length === 0) return PaginationDisplayType.Empty
  const { total, rows } = pages[length - 1]
  if (total === 0 && rows.length === 0) return PaginationDisplayType.Empty
  return PaginationDisplayType.Data
}
